import { useEffect, useState } from "react";
import { FileWarning, Plus, X, ChevronRight, ChevronLeft, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ACTA_ADMINISTRATIVA } from "@/constants/testIds";
import { toast } from "sonner";

const STEPS = ["Hechos", "Fundamento", "Testigos", "Versión del trabajador", "Revisión"];

const composeHechos = (incidents, selectedIds) =>
  incidents
    .filter((inc) => selectedIds.includes(inc.id))
    .map((inc) => `El día ${inc.incident_date}: ${inc.description}`)
    .join("\n");

// Guía Interactiva para levantar un Acta Administrativa desde una o más incidencias ya
// registradas. Cada paso explica, en lenguaje simple, qué exige la ley incluir (hechos con
// fecha/hora, fundamento, garantía de audiencia, testigos) — pensado para un admin sin
// formación legal. El documento resultante entra al mismo flujo de firma física + respaldo
// digital que ya usan los contratos, y lleva el mismo aviso de "pendiente de validación por
// abogado laboral" que el resto de la Biblioteca de Documentos.
export function ActaAdministrativaWizard({ employee, incidents, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [selectedIncidentIds, setSelectedIncidentIds] = useState([]);
  const [hechos, setHechos] = useState("");
  const [fundamento, setFundamento] = useState("");
  const [testigos, setTestigos] = useState([{ nombre: "", puesto: "" }]);
  const [versionTrabajador, setVersionTrabajador] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [hora, setHora] = useState("");
  const [lugar, setLugar] = useState("");
  const [levanto, setLevanto] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setSelectedIncidentIds([]);
      setHechos("");
      setFundamento("");
      setTestigos([{ nombre: "", puesto: "" }]);
      setVersionTrabajador("");
      setHora("");
      setLugar("");
      setLevanto("");
    }
  }, [open]);

  const toggleIncident = (incId) => {
    setSelectedIncidentIds((prev) => {
      const next = prev.includes(incId) ? prev.filter((i) => i !== incId) : [...prev, incId];
      setHechos(composeHechos(incidents, next));
      const referencedPolicies = incidents.filter((inc) => next.includes(inc.id) && inc.policy_title).map((inc) => inc.policy_title);
      if (referencedPolicies.length > 0) setFundamento([...new Set(referencedPolicies)].map((p) => `Política: ${p}`).join("\n"));
      return next;
    });
  };

  const updateTestigo = (idx, field, value) => setTestigos((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  const addTestigo = () => setTestigos((prev) => [...prev, { nombre: "", puesto: "" }]);
  const removeTestigo = (idx) => setTestigos((prev) => prev.filter((_, i) => i !== idx));

  const canAdvance = () => {
    if (step === 0) return hechos.trim().length > 0;
    if (step === 1) return fundamento.trim().length > 0;
    if (step === 4) return hora.trim() && lugar.trim() && levanto.trim();
    return true;
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const testigosTexto = testigos.filter((t) => t.nombre.trim()).map((t) => `${t.nombre} — ${t.puesto || "sin puesto especificado"}`).join("\n") || "Sin testigos presentes.";
    try {
      const { data } = await api.post(`/document-templates/acta_administrativa/generate`, {
        employee_id: employee.id,
        extra_fields: {
          fecha_acta: fecha, hora_acta: hora, lugar_acta: lugar,
          hechos_texto: hechos, fundamento_texto: fundamento, testigos_texto: testigosTexto,
          version_trabajador_texto: versionTrabajador.trim() || "[ESPACIO PARA QUE LA PERSONA TRABAJADORA EXPONGA SU VERSIÓN DE LOS HECHOS DURANTE EL ACTA]", levanto_nombre_puesto: levanto,
        },
      });
      toast.success(data.message);
      setOpen(false);
      onGenerated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid={ACTA_ADMINISTRATIVA.openButton} size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta hover:bg-terracotta/5" onClick={() => setOpen(true)}>
        <FileWarning className="w-4 h-4 mr-1" /> Generar Acta Administrativa
      </Button>
      <DialogContent data-testid={ACTA_ADMINISTRATIVA.dialog} className="bg-bone max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Acta Administrativa — {employee.full_name}</DialogTitle>
        </DialogHeader>

        <div data-testid={ACTA_ADMINISTRATIVA.stepIndicator} className="flex items-center gap-1.5 mb-2">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-terracotta" : "bg-petroleum/10"}`} />
          ))}
        </div>
        <p className="text-xs font-semibold text-charcoal/50 uppercase tracking-wide">Paso {step + 1} de {STEPS.length} · {STEPS[step]}</p>

        {step === 0 && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-charcoal/60 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Documentar los hechos con <b>fecha, hora, lugar y participantes</b> fortalece su valor probatorio. El acta administrativa no sustituye los requisitos legales de una sanción o rescisión. Selecciona las incidencias que quieres incluir y luego completa la hora si hace falta.
            </p>
            <Label className="text-sm">Incidencias registradas de {employee.full_name}</Label>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {incidents.map((inc) => (
                <label key={inc.id} className="flex items-start gap-2 text-sm bg-card border border-petroleum/10 rounded-lg p-2.5 cursor-pointer">
                  <input data-testid={`${ACTA_ADMINISTRATIVA.incidentCheckbox}-${inc.id}`} type="checkbox" checked={selectedIncidentIds.includes(inc.id)} onChange={() => toggleIncident(inc.id)} className="mt-0.5" />
                  <span><b>{inc.incident_date}</b> — {inc.description}</span>
                </label>
              ))}
              {incidents.length === 0 && <p className="text-xs text-charcoal/50">Sin incidencias registradas. Puedes seguir y describir los hechos manualmente.</p>}
            </div>
            <Label className="text-sm">Hechos (agrega hora y lugar si los conoces)</Label>
            <Textarea data-testid={ACTA_ADMINISTRATIVA.hechosTextarea} rows={5} value={hechos} onChange={(e) => setHechos(e.target.value)} placeholder="Ej. El 10/02/2026 a las 09:15am, en el área de bodega, el trabajador..." />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-charcoal/60 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Indica qué política, cláusula del reglamento u obligación se habría incumplido. Sin este fundamento, el acta solo narra hechos sin conectarlos con una regla conocida por la persona trabajadora.
            </p>
            <Label className="text-sm">Política, reglamento o disposición incumplida</Label>
            <Textarea data-testid={ACTA_ADMINISTRATIVA.fundamentoTextarea} rows={5} value={fundamento} onChange={(e) => setFundamento(e.target.value)} placeholder="Ej. Política de Vacaciones, sección 4 (solicitud y respuesta)..." />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-charcoal/60 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Si alguien presenció los hechos, anótalo aquí — fortalece el acta. Si no hay testigos disponibles, puedes dejar esta sección vacía y se anotará explícitamente.
            </p>
            {testigos.map((t, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input data-testid={`${ACTA_ADMINISTRATIVA.testigoNombreInput}-${idx}`} placeholder="Nombre del testigo" value={t.nombre} onChange={(e) => updateTestigo(idx, "nombre", e.target.value)} />
                <Input data-testid={`${ACTA_ADMINISTRATIVA.testigoPuestoInput}-${idx}`} placeholder="Puesto" value={t.puesto} onChange={(e) => updateTestigo(idx, "puesto", e.target.value)} />
                {testigos.length > 1 && (
                  <Button data-testid={`${ACTA_ADMINISTRATIVA.removeTestigoButton}-${idx}`} size="sm" variant="ghost" onClick={() => removeTestigo(idx)}><X className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
            <Button data-testid={ACTA_ADMINISTRATIVA.addTestigoButton} size="sm" variant="outline" onClick={addTestigo}><Plus className="w-4 h-4 mr-1" /> Agregar testigo</Button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-charcoal/60 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Antes de firmar, la persona trabajadora debe poder exponer su versión de los hechos (garantía de audiencia). Si aún no se ha reunido con ella, deja este espacio en blanco — quedará marcado para completarse durante la reunión, antes de imprimir y firmar.
            </p>
            <Label className="text-sm">Versión de la persona trabajadora (opcional)</Label>
            <Textarea data-testid={ACTA_ADMINISTRATIVA.versionTrabajadorTextarea} rows={5} value={versionTrabajador} onChange={(e) => setVersionTrabajador(e.target.value)} placeholder="Déjalo vacío si se completará a mano durante la reunión..." />
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-charcoal/60 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Últimos datos del acta. Después de generarla, podrás enviarla al portal del empleado, imprimirla en PDF, y subir el respaldo digital cuando la firmen en físico — igual que el resto de documentos de firma.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input data-testid={ACTA_ADMINISTRATIVA.fechaInput} type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
              <div><Label className="text-xs">Hora</Label><Input data-testid={ACTA_ADMINISTRATIVA.horaInput} placeholder="Ej. 10:00 am" value={hora} onChange={(e) => setHora(e.target.value)} /></div>
            </div>
            <div><Label className="text-xs">Lugar</Label><Input data-testid={ACTA_ADMINISTRATIVA.lugarInput} value={lugar} onChange={(e) => setLugar(e.target.value)} /></div>
            <div><Label className="text-xs">Nombre y puesto de quien levanta el acta</Label><Input data-testid={ACTA_ADMINISTRATIVA.levantoInput} value={levanto} onChange={(e) => setLevanto(e.target.value)} placeholder="Ej. María González, Gerente de RH" /></div>
            <p className="text-xs text-charcoal/50 flex items-start gap-1.5"><ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Este documento queda pendiente de validación por abogado laboral antes de tener carácter definitivo, igual que el resto de la Biblioteca de Documentos.</p>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <Button data-testid={ACTA_ADMINISTRATIVA.backButton} variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid={ACTA_ADMINISTRATIVA.nextButton} disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)} className="bg-petroleum rounded-full">Siguiente <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid={ACTA_ADMINISTRATIVA.generateButton} disabled={!canAdvance() || generating} onClick={handleGenerate} className="bg-terracotta hover:bg-terracotta/90 rounded-full">
              {generating ? "Generando..." : "Generar Acta"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
